import {
  getInAppAutoCookie,
  getWebAuthCookie,
} from "src/utils/authCookieParser";
import { appConfig } from "src/config";
import { captureException } from "src/utils/error";
import { trackBuyflowEvent } from "src/utils/api/tracker";
import { isCompoundedMedTier } from "src/utils/plans";
import { isContinuityOfCareEnabled } from "src/utils/userSegment/features";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import {
  setContinuityOfCareChecked,
  setContinuityOfCareEligible,
} from "src/utils/redux/slices/currentSubscriptionData";
import type { ActionHandlerParams } from "src/pageDefinitions/actions";
import {
  getNoomSessionStorage,
  setNoomSessionStorage,
} from "src/utils/noomSessionStorage";
import { SessionStorageKey } from "src/utils/constants";
import { getAuthorizationHeader } from "src/api/utils";

type Subscription = {
  readonly id: string;
  planId: string;
};

/**
 * Searches for user subscriptions
 *
 * @returns Promise<Subscription[] | null> - Array of subscriptions or null if unable to fetch
 */
// TODO: [MGEXP-2837]: Migrate to offers service for subscription history checks.
// This direct API call to /billing/v2/subscriptions:search will be replaced
// with offers service integration.
export async function searchSubscriptions(): Promise<Subscription[] | null> {
  try {
    const inAppAuthCookie = getInAppAutoCookie();
    const webAuthCookie = getWebAuthCookie();

    const accessCode = inAppAuthCookie?.accessCode || webAuthCookie?.accessCode;

    if (!accessCode) {
      return null;
    }

    const authHeader = getAuthorizationHeader();
    if (!authHeader) {
      return null;
    }

    const response = await fetch(
      `${appConfig.COACH_SERVER_URL}/billing/v2/subscriptions:search?accessCode=${accessCode}&maxResults=50`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...authHeader,
        },
      }
    );

    if (!response.ok) {
      captureException(
        new Error(`Failed to search subscriptions: ${response.status}`),
        "searchSubscriptions-api-error"
      );
      return null;
    }

    const subscriptions: Subscription[] = await response.json();
    return subscriptions;
  } catch (err) {
    captureException(err, "searchSubscriptions-error");
    return null;
  }
}

/**
 * Checks user subscription history for compounded semaglutide subscriptions.
 * Sets continuityOfCareEligible flag in Redux if compounded history is found.
 * This flag is used to make users eligible for Personalized Dosing based on continuity of care.
 *
 * Also validates that the user hasn't entered a different email during the flow,
 * which would indicate a different person and prevent continuity of care from applying.
 */
// TODO: [MGEXP-2837]: This will use offers service instead of direct API calls.
const continuityCheckPromises = new WeakMap<
  ActionHandlerParams["store"],
  Promise<void>
>();

export async function checkAndSetContinuityOfCareStatus({
  store,
}: Pick<ActionHandlerParams, "store">): Promise<void> {
  if (!isContinuityOfCareEnabled()) {
    return;
  }

  const { email: surveyEmail } = getSurveyAnswers(store.getState());

  if (surveyEmail) {
    const inAppAuthCookie = getInAppAutoCookie();
    const webAuthCookie = getWebAuthCookie();
    const cookieEmail = inAppAuthCookie?.email || webAuthCookie?.email;

    if (!cookieEmail) {
      await store.dispatch(setContinuityOfCareEligible(false));
      return;
    }

    if (surveyEmail.toLowerCase() !== cookieEmail.toLowerCase()) {
      trackBuyflowEvent("ContinuityOfCareEmailMismatched", {});
      await store.dispatch(setContinuityOfCareEligible(false));
      return;
    }
  }

  const { currentSubscriptionData } = store.getState();

  // Check if we're in a new browser session
  const hasCheckedInSession = getNoomSessionStorage<boolean>(
    SessionStorageKey.continuityOfCareCheck
  );

  if (currentSubscriptionData.continuityOfCareChecked && hasCheckedInSession) {
    return;
  }

  const existingPromise = continuityCheckPromises.get(store);
  if (existingPromise) {
    await existingPromise;
    return;
  }

  const continuityCheckPromise = (async () => {
    // If it's a new session, reset the checked flag to ensure fresh check
    const isNewSession = !hasCheckedInSession;
    if (isNewSession) {
      setNoomSessionStorage(SessionStorageKey.continuityOfCareCheck, true);
      await store.dispatch(setContinuityOfCareChecked(false));
    }

    const subscriptions = await searchSubscriptions();

    if (!subscriptions) {
      await store.dispatch(setContinuityOfCareChecked(true));
      // Reset eligibility only in new session on first API failure to avoid using stale data from localStorage
      if (isNewSession) {
        await store.dispatch(setContinuityOfCareEligible(false));
      }
      return;
    }

    const hasCompoundedHistory = subscriptions.some((subscription) =>
      isCompoundedMedTier(subscription.planId)
    );

    if (hasCompoundedHistory) {
      await store.dispatch(setContinuityOfCareEligible(true));
    } else {
      await store.dispatch(setContinuityOfCareEligible(false));
    }

    await store.dispatch(setContinuityOfCareChecked(true));
  })();

  continuityCheckPromises.set(store, continuityCheckPromise);

  try {
    await continuityCheckPromise;
  } finally {
    continuityCheckPromises.delete(store);
  }
}
